import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Button, Space, Typography, message, Spin, Tooltip, Modal, Descriptions, Progress } from 'antd';
import {
  ReloadOutlined, InboxOutlined, TagsOutlined, InfoCircleOutlined,
  CloudServerOutlined, CheckCircleOutlined, CloseCircleOutlined,
  LinkOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import { registryAPI } from '../services/api';
import { gray, radius } from '../theme/tokens';

const { Title, Text } = Typography;

const GHCR_ORG = 'sappt';
const PKG_LABELS: Record<string, string> = {
  'sappt-backend':          'Backend',
  'sappt-frontend':         'Frontend (Admin)',
  'sappt-partner-frontend': 'Partner Frontend',
};
const PKG_COLORS: Record<string, string> = {
  'sappt-backend':          '#4f46e5',
  'sappt-frontend':         '#0891b2',
  'sappt-partner-frontend': '#f59e0b',
};

interface ImageInfo   { name: string; tags: string[]; tagCount: number; }
interface ManifestDetail {
  schemaVersion: number; mediaType: string; digest: string;
  totalSize: number; layerCount: number;
  layers: { digest: string; size: number }[];
}

const formatBytes = (bytes: number): string => {
  if (!bytes) return '-';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const isTimestamp = (t: string) => /^\d{14}$/.test(t);
const isSha       = (t: string) => t.startsWith('sha-');
// 빌드 태그(YYYYMMDDHHmmss)는 GitHub Actions에서 UTC로 생성됨 → KST(UTC+9)로 변환 표시
const formatTs    = (t: string) => {
  if (!isTimestamp(t)) return t;
  const utc = Date.UTC(
    +t.slice(0, 4), +t.slice(4, 6) - 1, +t.slice(6, 8),
    +t.slice(8, 10), +t.slice(10, 12), +t.slice(12, 14),
  );
  const k = new Date(utc + 9 * 3600 * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${k.getUTCFullYear()}-${p(k.getUTCMonth() + 1)}-${p(k.getUTCDate())} ${p(k.getUTCHours())}:${p(k.getUTCMinutes())} KST`;
};

const RegistryPage: React.FC = () => {
  const [images,  setImages]  = useState<ImageInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [healthy, setHealthy] = useState<boolean | null>(null);
  const [manifestModal, setManifestModal] = useState<{
    visible: boolean; image: string; tag: string; data: ManifestDetail | null; loading: boolean;
  }>({ visible: false, image: '', tag: '', data: null, loading: false });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [catalogRes, healthRes] = await Promise.all([
        registryAPI.catalog(),
        registryAPI.health(),
      ]);
      setHealthy(healthRes.data.status === 'healthy');
      const repos: string[] = catalogRes.data.repositories || [];
      const infos = await Promise.all(
        repos.map(async (name) => {
          try {
            const r = await registryAPI.tags(name);
            const tags: string[] = r.data.tags || [];
            return { name, tags, tagCount: tags.length };
          } catch {
            return { name, tags: [], tagCount: 0 };
          }
        })
      );
      setImages(infos);
    } catch {
      message.error('GHCR 연결에 실패했습니다.');
      setHealthy(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showManifest = async (imageName: string, tag: string) => {
    setManifestModal({ visible: true, image: imageName, tag, data: null, loading: true });
    try {
      const res = await registryAPI.manifest(imageName, tag);
      setManifestModal(p => ({ ...p, data: res.data, loading: false }));
    } catch {
      message.error('매니페스트 조회에 실패했습니다.');
      setManifestModal(p => ({ ...p, loading: false }));
    }
  };

  const totalTags = images.reduce((s, i) => s + i.tagCount, 0);

  const columns = [
    {
      title: '패키지',
      dataIndex: 'name',
      key: 'name',
      width: 260,
      render: (name: string) => {
        const color = PKG_COLORS[name] || '#64748b';
        const label = PKG_LABELS[name] || name;
        return (
          <Space>
            <div style={{
              width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0,
            }} />
            <div>
              <Text strong style={{ color: gray[800], fontSize: 13 }}>{label}</Text>
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  ghcr.io/{GHCR_ORG}/{name}
                </Text>
              </div>
            </div>
          </Space>
        );
      },
    },
    {
      title: '태그 수',
      dataIndex: 'tagCount',
      key: 'tagCount',
      width: 90,
      align: 'center' as const,
      render: (count: number) => <Tag color="blue">{count}개</Tag>,
    },
    {
      title: 'latest',
      key: 'latest',
      width: 110,
      align: 'center' as const,
      render: (_: any, record: ImageInfo) => {
        const hasLatest = record.tags.includes('latest');
        return hasLatest ? (
          <Tooltip title="latest 태그 상세 보기">
            <Tag
              icon={<CheckCircleOutlined />}
              color="success"
              style={{ cursor: 'pointer' }}
              onClick={() => showManifest(record.name, 'latest')}
            >
              latest
            </Tag>
          </Tooltip>
        ) : <Tag color="default">없음</Tag>;
      },
    },
    {
      title: '최근 빌드',
      key: 'recent',
      width: 200,
      render: (_: any, record: ImageInfo) => {
        const ts = record.tags.filter(isTimestamp)[0];
        if (!ts) return <Text type="secondary" style={{ fontSize: 12 }}>-</Text>;
        return (
          <Tooltip title="해당 빌드 상세 보기">
            <Space
              size={4}
              style={{ cursor: 'pointer' }}
              onClick={() => showManifest(record.name, ts)}
            >
              <ClockCircleOutlined style={{ color: gray[500], fontSize: 12 }} />
              <Text style={{ fontSize: 12, color: '#475569' }}>{formatTs(ts)}</Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: '태그 / SHA',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags: string[], record: ImageInfo) => {
        const shaTags = tags.filter(isSha);
        const tsTags  = tags.filter(isTimestamp);
        return (
          <Space wrap size={4}>
            {shaTags.map(tag => (
              <Tooltip key={tag} title={`SHA 빌드: ${tag} — 상세 보기`}>
                <Tag
                  icon={<TagsOutlined />}
                  color="geekblue"
                  style={{ cursor: 'pointer', fontSize: 11 }}
                  onClick={() => showManifest(record.name, tag)}
                >
                  {tag}
                </Tag>
              </Tooltip>
            ))}
            {tsTags.length > 1 && (
              <Tag color="default" style={{ fontSize: 11, color: gray[400] }}>
                +{tsTags.length - 1}개 이전 빌드
              </Tag>
            )}
            {tags.length === 0 && <Text type="secondary" style={{ fontSize: 12 }}>태그 없음</Text>}
          </Space>
        );
      },
    },
    {
      title: 'GHCR',
      key: 'link',
      width: 70,
      align: 'center' as const,
      render: (_: any, record: ImageInfo) => (
        <Tooltip title="GitHub 패키지 페이지 열기">
          <a
            href={`https://github.com/orgs/${GHCR_ORG}/packages/container/package/${record.name}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <LinkOutlined style={{ fontSize: 15, color: '#4f46e5' }} />
          </a>
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* 상단 통계 카드 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
        <Card size="small" style={{ borderRadius: radius.lg, border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: healthy ? '#f0fdf4' : healthy === false ? '#fef2f2' : '#f8fafc',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {healthy
                ? <CheckCircleOutlined  style={{ fontSize: 20, color: '#16a34a' }} />
                : healthy === false
                  ? <CloseCircleOutlined style={{ fontSize: 20, color: '#dc2626' }} />
                  : <CloudServerOutlined style={{ fontSize: 20, color: gray[400] }} />}
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>GHCR 상태</Text>
              <div style={{ fontWeight: 700, fontSize: 15, color: healthy ? '#16a34a' : healthy === false ? '#dc2626' : '#94a3b8' }}>
                {healthy ? 'Connected' : healthy === false ? 'Unreachable' : '확인 중...'}
              </div>
            </div>
          </div>
        </Card>
        <Card size="small" style={{ borderRadius: radius.lg, border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <InboxOutlined style={{ fontSize: 20, color: '#4f46e5' }} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>등록 패키지</Text>
              <div style={{ fontWeight: 700, fontSize: 15, color: gray[800] }}>{images.length}개</div>
            </div>
          </div>
        </Card>
        <Card size="small" style={{ borderRadius: radius.lg, border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#faf5ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TagsOutlined style={{ fontSize: 20, color: '#7c3aed' }} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>전체 태그</Text>
              <div style={{ fontWeight: 700, fontSize: 15, color: gray[800] }}>{totalTags}개</div>
            </div>
          </div>
        </Card>
      </div>

      {/* 이미지 목록 */}
      <Card
        title={
          <Space>
            <InboxOutlined style={{ color: '#4f46e5' }} />
            <span style={{ fontWeight: 600 }}>GHCR 이미지 목록</span>
            <Tag color="blue" style={{ fontSize: 11 }}>ghcr.io/{GHCR_ORG}</Tag>
          </Space>
        }
        extra={
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading} size="small">
            새로고침
          </Button>
        }
        style={{ borderRadius: radius.lg, border: '1px solid #e2e8f0' }}
        styles={{ header: { borderBottom: '1px solid #f1f5f9' } }}
      >
        <Table
          dataSource={images}
          columns={columns}
          rowKey="name"
          loading={loading}
          pagination={false}
          size="middle"
          locale={{ emptyText: 'GHCR에 등록된 이미지가 없습니다.' }}
        />
      </Card>

      {/* 매니페스트 상세 모달 */}
      <Modal
        title={
          <Space>
            <InfoCircleOutlined style={{ color: '#4f46e5' }} />
            <span>이미지 상세 정보</span>
            <Tag color="geekblue" style={{ fontSize: 11 }}>{manifestModal.image.split('/').pop()}:{manifestModal.tag}</Tag>
          </Space>
        }
        open={manifestModal.visible}
        onCancel={() => setManifestModal(p => ({ ...p, visible: false }))}
        footer={null}
        width={640}
        maskClosable={false}
      >
        {manifestModal.loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : manifestModal.data ? (
          <div>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="패키지">{manifestModal.image.split('/').pop()}</Descriptions.Item>
              <Descriptions.Item label="태그">
                <Tag color={isTimestamp(manifestModal.tag) ? 'cyan' : isSha(manifestModal.tag) ? 'geekblue' : 'green'}>
                  {manifestModal.tag}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="전체 크기">
                <Text strong style={{ color: '#4f46e5' }}>{formatBytes(manifestModal.data.totalSize)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="레이어 수">{manifestModal.data.layerCount}개</Descriptions.Item>
              <Descriptions.Item label="스키마" span={2}>v{manifestModal.data.schemaVersion}</Descriptions.Item>
              <Descriptions.Item label="Digest" span={2}>
                <Text code style={{ fontSize: 11, wordBreak: 'break-all' }}>{manifestModal.data.digest}</Text>
              </Descriptions.Item>
            </Descriptions>
            <Title level={5} style={{ marginBottom: 8 }}>레이어 구성</Title>
            {manifestModal.data.layers.map((layer, idx) => {
              const pct = manifestModal.data!.totalSize > 0
                ? Math.round((layer.size / manifestModal.data!.totalSize) * 100) : 0;
              return (
                <div key={idx} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                  borderBottom: idx < manifestModal.data!.layers.length - 1 ? '1px solid #f1f5f9' : 'none',
                }}>
                  <Tag style={{ minWidth: 44, textAlign: 'center' }}>L{idx + 1}</Tag>
                  <Progress percent={pct} size="small" style={{ flex: 1, marginBottom: 0 }}
                    strokeColor="#4f46e5" format={() => formatBytes(layer.size)} />
                </div>
              );
            })}
          </div>
        ) : (
          <Text type="secondary">데이터를 불러올 수 없습니다.</Text>
        )}
      </Modal>
    </div>
  );
};

export default RegistryPage;
